import { FadeUp } from "@/components/Reveal";
import { MENU_COURSES, SIGNATURE_COCKTAILS, BAR_PROGRAM } from "@/data/content";

const MenuList = ({ items, idPrefix }) => (
  <div className="max-w-[760px] mx-auto">
    {items.map((c, i) => (
      <FadeUp key={`${idPrefix}-${c.title}`} delay={i * 0.06}>
        <article
          className="menu-item text-center"
          data-testid={`${idPrefix}-item-${i}`}
        >
          <div className="menu-course-label">{c.label}</div>
          <h3 className="menu-course-title mt-2">{c.title}</h3>
          <p className="menu-course-desc mx-auto mt-3">{c.desc}</p>
        </article>
      </FadeUp>
    ))}
  </div>
);

export const FoodMenu = () => (
  <section
    id="menu"
    data-testid="food-menu-section"
    className="section-pad relative bg-[#0c0c0c]"
  >
    <div className="max-w-[1100px] mx-auto">
      <FadeUp className="text-center mb-16 md:mb-20">
        <div className="eyebrow-line justify-center inline-flex">Chef-Curated Menu</div>
        <h2
          className="font-serif-display text-[#FDFBF7] mt-6 leading-[1.05]"
          style={{ fontSize: "clamp(36px, 5vw, 56px)" }}
        >
          The evening's <span className="italic text-[#D4AF37]">menu.</span>
        </h2>
        <p className="text-[#A3A3A3] mt-6 max-w-[58ch] mx-auto leading-[1.7]">
          A shared, hearth-driven table — smoky bread to open, a crisp chopped
          salad, kebabs from the grill, and warm sides meant to be passed.
          Catered by Aba and served at the table by a private waiter.
        </p>
        <div className="mt-10 inline-flex flex-col items-center gap-2 px-6 py-4 border border-[#D4AF37]/30 bg-[#0f0f0f]">
          <span className="overline text-[10px]">Catered By</span>
          <span className="font-serif-display text-[#FDFBF7] text-xl md:text-2xl">
            <span className="italic text-[#D4AF37]">Aba</span>
          </span>
          <span className="text-[#A3A3A3] text-xs tracking-[0.3em] uppercase">
            West Loop · Chicago
          </span>
        </div>
      </FadeUp>

      <MenuList items={MENU_COURSES} idPrefix="course" />

      <FadeUp className="text-center mt-14">
        <p className="text-[#A3A3A3] text-sm tracking-wider">
          Wine pairing and zero-proof pairing available — noted at the table.
        </p>
      </FadeUp>
    </div>
  </section>
);

const BarColumn = ({ heading, groups, idx }) => (
  <FadeUp delay={idx * 0.08}>
    <div
      data-testid={`bar-column-${heading.toLowerCase().replace(/\s+/g, "-")}`}
      className="border-t border-[#D4AF37]/25 pt-8"
    >
      <div className="menu-course-label tracking-[0.5em]">{heading}</div>
      <div className="mt-6 space-y-6">
        {groups.map((g, gi) => (
          <div key={gi}>
            {g.sub && (
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">
                {g.sub}
              </div>
            )}
            <ul className="space-y-2.5">
              {g.items.map((it) => (
                <li
                  key={it}
                  className="font-serif-display text-[#FDFBF7] text-lg md:text-xl leading-snug"
                >
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </FadeUp>
);

export const CocktailMenu = () => (
  <section
    id="cocktails"
    data-testid="cocktail-menu-section"
    className="section-pad relative"
  >
    <div className="max-w-[1200px] mx-auto">
      <FadeUp className="text-center mb-16 md:mb-20">
        <div className="eyebrow-line justify-center inline-flex">Signature Cocktails</div>
        <h2
          className="font-serif-display text-[#FDFBF7] mt-6 leading-[1.05]"
          style={{ fontSize: "clamp(36px, 5vw, 56px)" }}
        >
          The bar, <span className="italic text-[#D4AF37]">programmed.</span>
        </h2>
        <p className="text-[#A3A3A3] mt-6 max-w-[56ch] mx-auto leading-[1.7]">
          A private bartender pouring two signature cocktails, plus a full
          wine, beer, and spirits program. Zero-proof options always present.
        </p>
      </FadeUp>

      {/* Signature cocktails — featured */}
      <MenuList items={SIGNATURE_COCKTAILS} idPrefix="cocktail" />

      {/* Full bar program — 4-column grid */}
      <FadeUp className="text-center mt-24 mb-14">
        <div className="eyebrow-line justify-center inline-flex">The Full Bar</div>
      </FadeUp>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
        {BAR_PROGRAM.map((col, i) => (
          <BarColumn key={col.heading} heading={col.heading} groups={col.groups} idx={i} />
        ))}
      </div>
    </div>
  </section>
);
